// Rows of the FEMP Risk, Responsibility and Performance (RRP) Matrix —
// Table 3-1 of the FEMP M&V Guidelines, Gen 4 (2023).
//
// Only rows this project has a sourced label for are named here. Where the
// taxonomy gives a category but no row title, the category is shown as the
// taxonomy states it and no title is invented: guessing a row name would be the
// same unpinned-reference problem the tool exists to flag.

interface RrpRow {
  /** Row number as printed in Table 3-1, e.g. "2.a". */
  row: string;
  category: 'Financial' | 'Operational' | 'Performance';
  /** Row title, only where a source names it. */
  title: string;
  /** Where the title comes from. */
  source: string;
}

const RRP_ROWS: RrpRow[] = [
  {
    row: '1.e',
    category: 'Financial',
    title: 'M&V confidence',
    source: 'Check catalogue, FM-02 RRP category',
  },
  {
    row: '2.a',
    category: 'Operational',
    title: 'Operating hours',
    source: 'Check catalogue, FM-01 where it appears',
  },
  {
    row: '2.b',
    category: 'Operational',
    title: 'Load',
    source: 'Check catalogue, FM-01 where it appears',
  },
  {
    row: '2.d',
    category: 'Operational',
    title: 'User participation',
    source: 'Check catalogue, FM-07 RRP category',
  },
];

const BY_ROW = new Map(RRP_ROWS.map((r) => [r.row, r]));

/**
 * Renders a taxonomy rrp_category string for display, adding row titles where
 * they are known.
 *
 *   "Operational 2.a / 2.b"      -> "Operational 2.a — Operating hours · 2.b — Load"
 *   "Financial 1.f"              -> "Financial 1.f"
 *   "cross-cutting"              -> "Cross-cutting — no single RRP row"
 */
export function formatRrpCategory(rrpCategory: string): string {
  const raw = rrpCategory.trim();
  if (/^cross-cutting$/i.test(raw)) return 'Cross-cutting — no single RRP row';

  const categoryMatch = /^(Financial|Operational|Performance)/i.exec(raw);
  const category = categoryMatch
    ? categoryMatch[1][0].toUpperCase() + categoryMatch[1].slice(1).toLowerCase()
    : '';

  // Row references look like 1.e, 2.a, 3.a, and may be listed or ranged.
  const rows = [...raw.matchAll(/\b(\d\.[a-z])\b/g)].map((m) => m[1]);
  if (rows.length === 0) return raw;

  const parts = rows.map((row) => {
    const known = BY_ROW.get(row);
    return known ? `${row} — ${known.title}` : row;
  });

  // Preserve a range as a range rather than implying two discrete rows.
  const isRange = /\d\.[a-z]\s*[–-]\s*\d\.[a-z]/.test(raw);
  const joined = isRange ? parts.join(' to ') : parts.join(' · ');
  const parenthetical = /\(([^)]+)\)/.exec(raw);

  return [category, joined].filter(Boolean).join(' ') +
    (parenthetical && !BY_ROW.get(rows[0]) ? ` (${parenthetical[1]})` : '');
}
